import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Lock, ArrowRight, Sparkles, ShoppingBag, ArrowUpRight } from "lucide-react";
import { useReaderAuth } from "@/contexts/ReaderAuthContext";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
    available: "Disponibile",
    coming: "In arrivo",
    reserved: "Riservato",
};

const STATUS_TONE = {
    available: "text-pvx-success bg-pvx-success/10 border-pvx-success/20",
    coming: "text-pvx-warning bg-pvx-warning/10 border-pvx-warning/20",
    reserved: "text-pvx-primary bg-pvx-primary/10 border-pvx-primary/20",
};

export default function ExtraLibraryPage() {
    const { reader } = useReaderAuth();
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const nav = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API_ROOT}/public/extra/books`);
                if (res.ok) {
                    const data = await res.json();
                    setBooks(data.items || []);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const onBookClick = (book) => {
        if (book.status === "coming") return;
        nav(`/extra/${book.id}`);
    };

    return (
        <div>
            {/* Hero */}
            <div className="mb-14 md:mb-20 max-w-4xl">
                <div className="pvx-eyebrow pvx-brand-blue mb-5 flex items-center gap-1.5">
                    <Sparkles size={12} strokeWidth={1.5} /> Area riservata lettori
                </div>
                <h1 className="pvx-display text-[38px] sm:text-[44px] lg:text-[56px] font-normal tracking-[-0.005em] leading-[1.08] mb-6">
                    Contenuti Extra <span className="pvx-brand-blue whitespace-nowrap">Prime Vision</span>
                </h1>
                <p className="text-base md:text-[17px] text-pvx-textmuted leading-relaxed max-w-2xl">
                    Risorse, quiz e strumenti operativi riservati ai lettori dei libri Prime Vision.
                    Ogni libro sblocca un pacchetto dedicato di download, guide e materiali di approfondimento.
                </p>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {[0, 1, 2].map(i => (
                        <div key={i} className="pvx-card p-5 h-[520px] animate-pulse bg-pvx-surface2/40" />
                    ))}
                </div>
            ) : books.length === 0 ? (
                <div className="pvx-card p-10 text-center" data-testid="extra-empty">
                    <BookOpen size={28} strokeWidth={1.5} className="mx-auto text-pvx-textsubtle mb-3" />
                    <div className="text-[14px] font-medium mb-1">Nessun libro pubblicato al momento</div>
                    <div className="text-[12.5px] text-pvx-textsubtle">Torna a trovarci a breve: nuove uscite in arrivo.</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10" data-testid="extra-books-grid">
                    {books.map((b) => (
                        <BookCard key={b.id} book={b} onOpen={() => onBookClick(b)} isReader={!!reader} />
                    ))}
                </div>
            )}
        </div>
    );
}

function BookCard({ book, onOpen, isReader }) {
    const disabled = book.status === "coming";
    const purchaseUrl = (book.purchase_url || "").trim();
    const purchaseLabel = (book.purchase_cta_text || "").trim() || "Acquista il libro";

    return (
        <div
            data-testid={`book-card-${book.id}`}
            className={`group relative pvx-card overflow-hidden flex flex-col transition-all duration-500 ${
                disabled ? "opacity-70" : "hover:-translate-y-1.5 hover:shadow-2xl"
            }`}
        >
            {/* Cover pane */}
            <button
                type="button"
                onClick={onOpen}
                disabled={disabled}
                aria-label={`Apri ${book.title}`}
                className={`relative w-full text-left aspect-[3/4] bg-[#F4F6FA] flex items-center justify-center overflow-hidden ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                style={{ backgroundImage: "linear-gradient(180deg, #F7F9FC 0%, #EEF2F8 100%)" }}
            >
                {book.cover_path ? (
                    <img
                        src={`${API_ROOT}/public/extra/cover/${book.id}`}
                        alt={book.title}
                        className="relative max-h-[85%] max-w-[78%] object-contain drop-shadow-[0_18px_35px_rgba(52,127,184,0.25)] transition-all duration-700 group-hover:scale-[1.03] group-hover:-translate-y-1"
                        data-testid={`book-cover-${book.id}`}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                ) : (
                    <BookOpen size={72} strokeWidth={1} className="relative text-pvx-primary/25" />
                )}
                {/* Status pill */}
                <div className={`absolute top-4 right-4 text-[10.5px] font-medium px-2.5 py-1 rounded-full border backdrop-blur-sm ${STATUS_TONE[book.status] || "text-pvx-textsubtle bg-pvx-surface2 border-pvx-border"}`}>
                    {STATUS_LABEL[book.status] || book.status}
                </div>
            </button>

            {/* Text pane */}
            <div className="p-6 md:p-7 flex-1 flex flex-col">
                {book.author_name && (
                    <div className="pvx-eyebrow text-pvx-textsubtle mb-2">di {book.author_name}</div>
                )}
                <div className="pvx-serif text-[22px] leading-tight tracking-tight mb-1.5">{book.title}</div>
                {book.subtitle && (
                    <div className="text-[13px] text-pvx-textmuted italic mb-4">{book.subtitle}</div>
                )}
                {book.short_description && (
                    <p className="text-[13px] text-pvx-textmain/80 line-clamp-3 mb-5 leading-relaxed">
                        {book.short_description}
                    </p>
                )}

                {/* Actions */}
                <div className="mt-auto space-y-2.5">
                    {purchaseUrl && (
                        <a
                            href={purchaseUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`book-purchase-${book.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="group/buy relative flex items-center justify-between w-full pl-4 pr-3 py-3 rounded-[10px] bg-pvx-primary text-white text-[13.5px] font-medium overflow-hidden hover:opacity-95 transition-all"
                        >
                            <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/buy:translate-x-full transition-transform duration-700" />
                            <span className="relative flex items-center gap-2">
                                <ShoppingBag size={14} strokeWidth={1.75} />
                                {purchaseLabel}
                            </span>
                            <ArrowUpRight size={15} strokeWidth={1.75} className="relative transition-transform group-hover/buy:translate-x-0.5 group-hover/buy:-translate-y-0.5" />
                        </a>
                    )}
                    <button
                        type="button"
                        onClick={onOpen}
                        disabled={disabled}
                        data-testid={`book-open-${book.id}`}
                        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-[10px] text-[13px] font-medium transition-all ${
                            disabled ? "text-pvx-textsubtle cursor-not-allowed" :
                            purchaseUrl ? "text-pvx-primary hover:bg-pvx-primary/[0.06]" :
                            "bg-pvx-primary text-white hover:opacity-90"
                        }`}
                    >
                        <span className="flex items-center gap-1.5">
                            {disabled ? <><Lock size={13} strokeWidth={1.5} /> Prossima uscita</>
                             : isReader ? "Apri contenuti extra"
                             : "Accedi per sbloccare i contenuti"}
                        </span>
                        {!disabled && <ArrowRight size={13.5} strokeWidth={1.75} className="transition-transform group-hover:translate-x-0.5" />}
                    </button>
                </div>
            </div>
        </div>
    );
}
